import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useAllLogs, useProfile, dayNumberFromStartDate, useAiPlan } from '../lib/hooks'
import { TOTAL_DAYS, getEffectiveDayPlan } from '../data/plan'
import { computeDayNutrition } from '../lib/mealOverrides'
import { getExercisePRs, formatExerciseLog } from '../lib/exerciseStats'
import { buildWeeklyReviews } from '../lib/weeklyReview'

const MEASUREMENT_FIELDS = [
  { key: 'waistCm', label: 'Waist', color: 'var(--h-accent)' },
  { key: 'chestCm', label: 'Chest', color: 'var(--h-text)' },
  { key: 'armsCm', label: 'Arms', color: '#ff9d5c' },
  { key: 'thighsCm', label: 'Thighs', color: '#5c9dff' },
]

const TIPS = [
  'Protein is the priority - hit it every day, never skip the whey or Greek yogurt.',
  'Steps matter as much as the gym. Walk after meals.',
  'Weigh in weekly, same morning, empty stomach. Track the weekly average, not daily noise.',
  'One relaxed meal a week is fine - back to plan next meal.',
]

function isDayComplete(log, dayNumber, aiPlan) {
  const plan = getEffectiveDayPlan(dayNumber, aiPlan)
  return plan.exercises.length > 0 && plan.exercises.every((e) => log?.exerciseChecks?.[e.name])
}

export default function ProgressPage() {
  const { logs } = useAllLogs()
  const { profile } = useProfile()
  const { aiPlan } = useAiPlan()
  const currentDay = profile?.startDate ? dayNumberFromStartDate(profile.startDate) : 1
  const week = Math.floor((currentDay - 1) / 7) + 1

  const weightSeries = Object.values(logs)
    .filter((l) => l.weightKg != null)
    .sort((a, b) => a.dayNumber - b.dayNumber)
    .map((l) => ({ day: l.dayNumber, weight: l.weightKg }))

  const firstWeight = weightSeries[0]?.weight
  const lastWeight = weightSeries[weightSeries.length - 1]?.weight
  const delta = firstWeight != null && lastWeight != null ? (lastWeight - firstWeight).toFixed(1) : null

  const firstDay = weightSeries[0]?.day
  const lastDay = weightSeries[weightSeries.length - 1]?.day
  const weeksSpan = firstDay != null && lastDay != null && lastDay > firstDay ? (lastDay - firstDay) / 7 : null
  const weeklyRate = weeksSpan ? (lastWeight - firstWeight) / weeksSpan : null

  // Weekly review: rate of change over the last 7 / 14 days specifically
  // (not the all-time average), per the plan's own adjustment rules.
  const rateOverWindow = (windowDays) => {
    const inWindow = weightSeries.filter((w) => w.day > currentDay - windowDays && w.day <= currentDay)
    if (inWindow.length < 2) return null
    const span = inWindow[inWindow.length - 1].day - inWindow[0].day
    if (span <= 0) return null
    return ((inWindow[inWindow.length - 1].weight - inWindow[0].weight) / span) * 7
  }
  const last7Rate = rateOverWindow(7)
  const last14Rate = rateOverWindow(14)

  let review = null
  if (last7Rate != null) {
    if (last7Rate <= -0.7) {
      review = { tag: 'TOO FAST', tone: 'warn', message: `Losing ${Math.abs(last7Rate).toFixed(2)} kg/week - faster than the 0.4-0.5 target. Add ~150 kcal (extra roti or fruit); too-fast loss eats muscle.` }
    } else if (last7Rate >= -0.15 && last14Rate != null && last14Rate >= -0.15) {
      review = { tag: 'STALLED', tone: 'warn', message: `Barely moved the last 2 weeks. Tighten portions slightly or add 1,000 steps.` }
    } else if (last7Rate >= -0.15) {
      review = { tag: 'WATCHING', tone: 'neutral', message: 'This week is flat - give it a few more days before changing anything.' }
    } else {
      review = { tag: 'ON TRACK', tone: 'good', message: `${Math.abs(last7Rate).toFixed(2)} kg/week - right in the 0.4-0.5 target range. Keep doing exactly this.` }
    }
  }

  const elapsedDays = Math.max(currentDay - 1, 0)
  const daysDone = Object.keys(logs).filter((d) => Number(d) < currentDay && isDayComplete(logs[d], Number(d), aiPlan)).length
  const adherence = elapsedDays > 0 ? Math.round((daysDone / elapsedDays) * 100) : 0

  // Streaks use the same "day complete" definition as adherence (all
  // exercises checked) - current counts backward from yesterday, best scans
  // the whole history so far.
  let currentStreak = 0
  for (let d = currentDay - 1; d >= 1; d--) {
    if (isDayComplete(logs[String(d)], d, aiPlan)) currentStreak++
    else break
  }
  let bestStreak = 0
  let run = 0
  for (let d = 1; d < currentDay; d++) {
    if (isDayComplete(logs[String(d)], d, aiPlan)) {
      run++
      bestStreak = Math.max(bestStreak, run)
    } else {
      run = 0
    }
  }

  const nutritionSeries = Object.values(logs)
    .map((l) => ({ day: l.dayNumber, ...computeDayNutrition(l.dayNumber, l, aiPlan) }))
    .filter((d) => d.checkedKcal > 0)
    .sort((a, b) => a.day - b.day)

  const exercisePRs = getExercisePRs(logs)
  const prList = Object.entries(exercisePRs).sort((a, b) => a[0].localeCompare(b[0]))
  const weeklyReviews = buildWeeklyReviews(logs, currentDay, aiPlan, exercisePRs)

  const measurementSeries = Object.values(logs)
    .filter((l) => MEASUREMENT_FIELDS.some((f) => l[f.key] != null))
    .sort((a, b) => a.dayNumber - b.dayNumber)
    .map((l) => ({ day: l.dayNumber, waistCm: l.waistCm, chestCm: l.chestCm, armsCm: l.armsCm, thighsCm: l.thighsCm }))
  const measurementFieldsWithData = MEASUREMENT_FIELDS.filter(
    (f) => measurementSeries.filter((m) => m[f.key] != null).length >= 2,
  )

  // Projection uses the most recent trend when available (last7Rate) since
  // it reflects what you're doing right now, falling back to the all-time
  // rate early on when there isn't a week of data yet.
  const goalRate = last7Rate ?? weeklyRate
  let goalProjection = null
  if (profile?.targetWeightKg != null && lastWeight != null && goalRate != null && goalRate < 0 && lastWeight > profile.targetWeightKg) {
    const kgToLose = lastWeight - profile.targetWeightKg
    const daysNeeded = Math.round((kgToLose / Math.abs(goalRate)) * 7)
    const targetDate = new Date()
    targetDate.setDate(targetDate.getDate() + daysNeeded)
    goalProjection = { kgToLose: kgToLose.toFixed(1), daysNeeded, targetDate, usingRecentRate: last7Rate != null }
  }

  return (
    <div className="section">
      <span className="plan-eyebrow">● WEEK {week}</span>
      <h1 className="display-heading" style={{ margin: '16px 0 32px' }}>Progress</h1>

      <div className="stats-row">
        <div className="stat-block is-hero">
          <span className="stat-block-label">DAY</span>
          <span className="stat-block-value">{currentDay}<small>/{TOTAL_DAYS}</small></span>
        </div>
        <div className="stat-block">
          <span className="stat-block-label">ADHERENCE</span>
          <span className="stat-block-value">{adherence}%</span>
        </div>
        <div className="stat-block">
          <span className="stat-block-label">WEIGHT CHANGE</span>
          <span className={`stat-block-value ${delta == null ? 'is-empty' : ''}`}>
            {delta != null ? `${delta > 0 ? '+' : ''}${delta}` : '—'}
          </span>
        </div>
        <div className="stat-block">
          <span className="stat-block-label">STREAK</span>
          <span className={`stat-block-value ${currentStreak === 0 ? 'is-empty' : ''}`}>
            {currentStreak}<small>{bestStreak > currentStreak ? ` · best ${bestStreak}` : ''}</small>
          </span>
        </div>
      </div>

      <div className="card-grid">
        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 14 }}>
            <span className="card-index">01</span>
            <h2 className="card-title">Weekly review</h2>
          </div>
          {review ? (
            <>
              <span className={`review-tag tone-${review.tone}`}>{review.tag}</span>
              <p className="review-message">{review.message}</p>
            </>
          ) : (
            <p className="card-subtitle">Log your weight on a few more days this week to get a read.</p>
          )}
          {weeklyReviews.length === 0 ? (
            <p className="card-subtitle" style={{ marginTop: 14 }}>Your week-by-week recap will build up here as days pass.</p>
          ) : (
            <div className="weekly-review-list">
              {weeklyReviews.map((w) => (
                <div key={w.week} className="weekly-review-row">
                  <div className="weekly-review-row-head">
                    <span className="weekly-review-week">WEEK {w.week}{w.isCurrent ? ' · SO FAR' : ''}</span>
                    <span className="weekly-review-adherence">{w.adherence}% adherence</span>
                  </div>
                  <div className="weekly-review-stats">
                    <span>{w.weightChange != null ? `${w.weightChange > 0 ? '+' : ''}${w.weightChange}kg` : 'no weigh-ins'}</span>
                    <span>{w.avgKcal != null ? `${w.avgKcal} kcal avg` : '—'}</span>
                    <span>{w.avgProtein != null ? `${w.avgProtein}g protein avg` : '—'}</span>
                    <span>{w.prNames.length > 0 ? `${w.prNames.length} PR${w.prNames.length > 1 ? 's' : ''}` : 'no PRs'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 18 }}>
            <span className="card-index">02</span>
            <h2 className="card-title">Body weight trend</h2>
          </div>
          {weightSeries.length < 2 ? (
            <div className="trend-placeholder">
              <div className="trend-placeholder-line" />
              <span className="trend-placeholder-text">Log your weight on a few more days to see the trend.</span>
            </div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={weightSeries} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--h-border)" />
                  <XAxis dataKey="day" stroke="var(--h-text-secondary)" fontSize={12} tickLine={false} />
                  <YAxis stroke="var(--h-text-secondary)" fontSize={12} tickLine={false} domain={['dataMin - 1', 'dataMax + 1']} />
                  <Tooltip
                    contentStyle={{ background: 'var(--h-surface)', border: '1px solid var(--h-border)', borderRadius: 8 }}
                    labelStyle={{ color: 'var(--h-text)' }}
                    labelFormatter={(d) => `Day ${d}`}
                    formatter={(v) => [`${v} kg`, 'Weight']}
                  />
                  <Line type="monotone" dataKey="weight" stroke="var(--h-accent)" strokeWidth={2} dot={{ r: 3, fill: 'var(--h-accent)' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {weeklyRate != null && (
            <p className="trend-note">
              ALL-TIME TREND: {weeklyRate > 0 ? '+' : ''}{weeklyRate.toFixed(2)} KG/WEEK · TARGET -0.4 TO -0.5 KG/WEEK
            </p>
          )}
        </div>

        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 14 }}>
            <span className="card-index">03</span>
            <h2 className="card-title">Goal projection</h2>
          </div>
          {!profile?.targetWeightKg ? (
            <p className="card-subtitle">Set a target weight in Settings to see a projection.</p>
          ) : goalProjection ? (
            <>
              <p className="goal-projection-headline">
                At this rate, you'll hit <strong>{profile.targetWeightKg}kg</strong> in ~{goalProjection.daysNeeded} days
              </p>
              <p className="card-subtitle">
                {goalProjection.targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {goalProjection.kgToLose}kg to go · based on {goalProjection.usingRecentRate ? 'the last 7 days' : 'the all-time'} trend
              </p>
            </>
          ) : (
            <p className="card-subtitle">
              {lastWeight == null
                ? 'Log your weight to get a projection.'
                : lastWeight <= profile.targetWeightKg
                  ? "You've already hit your target weight."
                  : "Not losing weight right now, so there's no projection yet - check the weekly review."}
            </p>
          )}
        </div>

        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 18 }}>
            <span className="card-index">04</span>
            <h2 className="card-title">Nutrition trend</h2>
          </div>
          {nutritionSeries.length < 2 ? (
            <div className="trend-placeholder">
              <div className="trend-placeholder-line" />
              <span className="trend-placeholder-text">Check off a few more meals to see the trend.</span>
            </div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={nutritionSeries} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--h-border)" />
                  <XAxis dataKey="day" stroke="var(--h-text-secondary)" fontSize={12} tickLine={false} />
                  <YAxis yAxisId="kcal" stroke="var(--h-text-secondary)" fontSize={12} tickLine={false} />
                  <YAxis yAxisId="protein" orientation="right" stroke="var(--h-accent)" fontSize={12} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--h-surface)', border: '1px solid var(--h-border)', borderRadius: 8 }}
                    labelStyle={{ color: 'var(--h-text)' }}
                    labelFormatter={(d) => `Day ${d}`}
                    formatter={(v, name) => [name === 'checkedKcal' ? `${v} kcal` : `${v}g`, name === 'checkedKcal' ? 'Kcal' : 'Protein']}
                  />
                  <Line yAxisId="kcal" type="monotone" dataKey="checkedKcal" stroke="var(--h-text)" strokeWidth={2} dot={{ r: 3, fill: 'var(--h-text)' }} />
                  <Line yAxisId="protein" type="monotone" dataKey="checkedProtein" stroke="var(--h-accent)" strokeWidth={2} dot={{ r: 3, fill: 'var(--h-accent)' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="trend-note">
            <span style={{ color: 'var(--h-text)' }}>■</span> KCAL EATEN &nbsp; <span style={{ color: 'var(--h-accent)' }}>■</span> PROTEIN EATEN (RIGHT AXIS)
          </p>
        </div>

        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 18 }}>
            <span className="card-index">05</span>
            <h2 className="card-title">Body measurements</h2>
          </div>
          {measurementFieldsWithData.length === 0 ? (
            <div className="trend-placeholder">
              <div className="trend-placeholder-line" />
              <span className="trend-placeholder-text">Log waist, chest, arms or thighs from Today's Daily Log to see the trend.</span>
            </div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={measurementSeries} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--h-border)" />
                  <XAxis dataKey="day" stroke="var(--h-text-secondary)" fontSize={12} tickLine={false} />
                  <YAxis stroke="var(--h-text-secondary)" fontSize={12} tickLine={false} domain={['dataMin - 2', 'dataMax + 2']} />
                  <Tooltip
                    contentStyle={{ background: 'var(--h-surface)', border: '1px solid var(--h-border)', borderRadius: 8 }}
                    labelStyle={{ color: 'var(--h-text)' }}
                    labelFormatter={(d) => `Day ${d}`}
                    formatter={(v, name) => [`${v} cm`, MEASUREMENT_FIELDS.find((f) => f.key === name)?.label || name]}
                  />
                  <Legend
                    wrapperStyle={{ fontFamily: 'var(--h-font-mono)', fontSize: 11, letterSpacing: '0.08em' }}
                    formatter={(name) => MEASUREMENT_FIELDS.find((f) => f.key === name)?.label || name}
                  />
                  {measurementFieldsWithData.map((f) => (
                    <Line key={f.key} type="monotone" dataKey={f.key} stroke={f.color} strokeWidth={2} dot={{ r: 3, fill: f.color }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <PhotoGallery logs={logs} />

        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 14 }}>
            <span className="card-index">07</span>
            <h2 className="card-title">Personal records</h2>
          </div>
          {prList.length === 0 ? (
            <p className="card-subtitle">Log a weight next to an exercise on Today to start tracking records.</p>
          ) : (
            <div className="pr-list">
              {prList.map(([name, pr]) => (
                <div key={name} className="pr-row">
                  <span className="pr-name">{name}</span>
                  <span className="pr-value">{formatExerciseLog(pr)}</span>
                  <span className="pr-day">DAY {String(pr.dayNumber).padStart(2, '0')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title-group" style={{ marginBottom: 8 }}>
            <span className="card-index">08</span>
            <h2 className="card-title">Making the cut work</h2>
          </div>
          <ul className="tips-list">
            {TIPS.map((text, i) => (
              <li key={i} className="tip-row">
                <span className="tip-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="tip-text">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function PhotoGallery({ logs }) {
  const photos = Object.values(logs)
    .filter((l) => l.photo)
    .sort((a, b) => a.dayNumber - b.dayNumber)

  return (
    <div className="card">
      <div className="card-title-group" style={{ marginBottom: 14 }}>
        <span className="card-index">06</span>
        <h2 className="card-title">Progress photos</h2>
      </div>
      {photos.length === 0 ? (
        <p className="card-subtitle">No photos yet - add one from Today's Daily Log to start a visual record.</p>
      ) : (
        <div className="photo-gallery">
          {photos.map((l) => (
            <a key={l.dayNumber} href={l.photo} target="_blank" rel="noopener noreferrer" className="photo-thumb">
              <img src={l.photo} alt={`Day ${l.dayNumber}`} />
              <span>DAY {String(l.dayNumber).padStart(2, '0')}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
